import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, PencilSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { EmployeeProfileSkeleton } from '../../components/directory-skeletons/directory-skeletons';
import { EmployeeProfileView } from '../../components/employee-profile-view/employee-profile-view';
import { useGetEmployeeById } from '../../hooks/use-employees';
import { Employee, EmployeesPagingResult } from '../../types/employee.types';

export const EmployeeDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { employeeId } = useParams<{ employeeId: string }>();
  const canUpdate = useHasPermission(HRM_PERMISSIONS.DIRECTORY.UPDATE);

  const { data, isLoading } = useGetEmployeeById(employeeId);
  const employee: Employee | undefined = (data as { Employees?: EmployeesPagingResult })?.Employees
    ?.items?.[0];

  // Back to the directory, with the page's title (the old portal's "← Employee Details").
  const header = (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0"
        aria-label={t('BACK')}
        title={t('BACK')}
        onClick={() => navigate('/hr/directory')}
      >
        <ArrowLeft className="size-5" />
      </Button>
      <h1 className="min-w-0 truncate text-lg font-semibold">{t('EMPLOYEE_DETAILS')}</h1>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-4">
        {header}
        <EmployeeProfileSkeleton />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="flex w-full flex-col gap-4">
        {header}
        <p className="text-sm text-muted-foreground">{t('EMPLOYEE_NOT_FOUND')}</p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {header}
      <EmployeeProfileView
        employee={employee}
        actions={
          canUpdate && (
            // A pencil icon in the card's corner on a phone.
            <Button
              variant="outline"
              aria-label={t('EDIT')}
              title={t('EDIT')}
              className="max-sm:size-9 max-sm:p-0"
              onClick={() => navigate(`/hr/directory/${employee.ItemId}/create`)}
            >
              <PencilSimple className="size-4" />
              <span className="max-sm:sr-only">{t('EDIT')}</span>
            </Button>
          )
        }
      />
    </div>
  );
};
